import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { Clock, ShieldCheck, User, Calendar, Download, Headphones, QrCode, ArrowRight, CheckCircle2, AlertTriangle } from "lucide-react";
import QstLayout, { AiSafetyBadge } from "./QstLayout";
import QstAssistantWidget from "./QstAssistantWidget";
import QstTranslationSwitcher from "./QstTranslationSwitcher";
import { qstLessonDetail } from "./api";

const CAT_LABEL = { "quality-control": "Quality Control", "safety": "Safety", "loss-prevention": "Loss Prevention" };
const CAT_COLOR = {
  "quality-control": { bg: "bg-orange-50", fg: "text-[#F97316]", ring: "ring-orange-200" },
  "safety":          { bg: "bg-red-50",    fg: "text-red-700",   ring: "ring-red-200" },
  "loss-prevention": { bg: "bg-emerald-50",fg: "text-emerald-700", ring: "ring-emerald-200" },
};

export default function QstLessonPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [presenting, setPresenting] = useState(false);
  const [translated, setTranslated] = useState(null);   // {lang, content, reviewed}

  useEffect(() => {
    window.scrollTo(0, 0);
    setTranslated(null);
    qstLessonDetail(slug)
      .then((d) => { setData(d); document.title = `${d.lesson.title} — QST`; })
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return <Navigate to="/qst/library" replace />;
  if (!data) return <QstLayout><div className="p-20 text-center">Loading…</div></QstLayout>;
  const { lesson: baseLesson, trade, related } = data;
  // Merge the base lesson with any translated fields so the same JSX renders.
  const lesson = translated
    ? { ...baseLesson, ...translated.content }
    : baseLesson;
  const c = CAT_COLOR[lesson.category];

  if (presenting) return <PresentationMode lesson={lesson} onExit={() => setPresenting(false)} />;

  return (
    <QstLayout>
      <QstAssistantWidget context={{ page: "lesson", trade: lesson.trade, lessonTitle: lesson.title }} />

      <section className="max-w-4xl mx-auto px-6 pt-10 pb-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <Link to={`/qst/trades/${baseLesson.trade}`} className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-slate-500 hover:text-[#0B2545] font-bold">
            ← Back to {trade?.name || 'Trade'}
          </Link>
          <QstTranslationSwitcher
            slug={baseLesson.slug}
            onContentChange={(lang, content, meta) => {
              if (lang === "en") setTranslated(null);
              else setTranslated({ lang, content, ...meta });
            }}
          />
        </div>
        <div className={`mt-4 inline-flex items-center gap-2 rounded-full ${c.bg} ${c.fg} px-3 py-1 text-[11px] font-bold uppercase tracking-widest ring-1 ${c.ring}`}>
          {CAT_LABEL[lesson.category]}
        </div>
        <h1 className="mt-3 text-3xl sm:text-5xl font-black text-[#0B2545] tracking-tight leading-tight">{lesson.title}</h1>
        <p className="mt-4 text-lg text-slate-700 leading-relaxed">{lesson.objective}</p>
        <div className="mt-5 flex flex-wrap items-center gap-4 text-xs text-slate-600">
          <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {lesson.estimated_minutes} min</span>
          <span className="inline-flex items-center gap-1"><User className="w-3.5 h-3.5" /> {lesson.author}</span>
          <span className="inline-flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5 text-emerald-600" /> Reviewed by {lesson.reviewer}, {lesson.reviewer_role}</span>
          <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> v{lesson.version} · reviewed {lesson.last_reviewed}</span>
        </div>

        <div className="mt-6 flex flex-wrap gap-2">
          <button onClick={() => setPresenting(true)}
            className="inline-flex items-center gap-2 rounded-md bg-[#F97316] hover:bg-[#EA580C] text-white px-5 py-2.5 font-bold shadow-sm"
            data-testid="qst-lesson-present-btn">
            Present to Crew <ArrowRight className="w-4 h-4" />
          </button>
          <button className="inline-flex items-center gap-2 rounded-md border border-slate-300 hover:bg-slate-50 text-slate-800 px-5 py-2.5 font-bold">
            <Headphones className="w-4 h-4" /> Listen
          </button>
          <button className="inline-flex items-center gap-2 rounded-md border border-slate-300 hover:bg-slate-50 text-slate-800 px-5 py-2.5 font-bold">
            <Download className="w-4 h-4" /> Download PDF
          </button>
          <button className="inline-flex items-center gap-2 rounded-md border border-slate-300 hover:bg-slate-50 text-slate-800 px-5 py-2.5 font-bold">
            <QrCode className="w-4 h-4" /> QR Code
          </button>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 py-6 space-y-8">
        <LessonBlock title="The Problem" body={lesson.problem} icon={AlertTriangle} accent="#DC2626" />

        <LessonList title="Root Causes" items={lesson.root_causes} icon={AlertTriangle} accent="#DC2626" />

        <LessonList title="Correct Procedure" items={lesson.correct_procedure} icon={CheckCircle2} accent="#059669" numbered />

        {lesson.prevention_steps && (
          <LessonList title="Prevention Steps" items={lesson.prevention_steps} icon={ShieldCheck} accent="#F97316" />
        )}

        {lesson.discussion_questions && lesson.discussion_questions.length > 0 && (
          <section className="rounded-xl border-2 border-[#0B2545] bg-[#0B2545]/[.03] p-6">
            <h2 className="text-lg font-bold text-[#0B2545]">Discussion Questions</h2>
            <p className="mt-1 text-sm text-slate-600">Use these to engage the crew and connect the lesson to today's site conditions.</p>
            <ol className="mt-4 space-y-3">
              {lesson.discussion_questions.map((q, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="w-7 h-7 rounded-full bg-[#0B2545] text-white text-xs font-black grid place-items-center flex-shrink-0">{i + 1}</span>
                  <span className="text-slate-800">{q}</span>
                </li>
              ))}
            </ol>
          </section>
        )}

        {lesson.knowledge_check && lesson.knowledge_check.length > 0 && (
          <section className="rounded-xl border border-slate-200 bg-white p-6">
            <h2 className="text-lg font-bold text-[#0B2545]">Knowledge Check</h2>
            <div className="mt-4 space-y-4">
              {lesson.knowledge_check.map((kc, i) => (
                <details key={i} className="rounded-md border border-slate-200 p-4">
                  <summary className="cursor-pointer font-semibold text-slate-800">{kc.q}</summary>
                  <div className="mt-2 text-sm text-emerald-800 bg-emerald-50 rounded-md px-3 py-2">{kc.a}</div>
                </details>
              ))}
            </div>
          </section>
        )}

        <div className="rounded-xl border border-slate-200 bg-slate-50 p-6">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-600">Sources & Review</div>
          <div className="mt-2 text-sm text-slate-700">
            Author: <strong>{lesson.author}</strong> ({lesson.author_experience_years}+ yrs) · Reviewed by <strong>{lesson.reviewer}, {lesson.reviewer_role}</strong>.
          </div>
          <div className="mt-1 text-xs text-slate-600">
            Published {lesson.published_at} · Last technical review {lesson.last_reviewed} · Version {lesson.version} · Languages: {lesson.languages.join(', ').toUpperCase()}
          </div>
          <div className="mt-4"><AiSafetyBadge /></div>
        </div>
      </section>

      {related && related.length > 0 && (
        <section className="max-w-4xl mx-auto px-6 py-8">
          <h2 className="text-lg font-bold text-[#0B2545]">Related Lessons</h2>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            {related.map((r) => (
              <Link key={r.slug} to={`/qst/lessons/${r.slug}`}
                className="rounded-lg border border-slate-200 bg-white p-4 hover:shadow-md transition">
                <div className="text-sm font-bold text-[#0B2545]">{r.title}</div>
                <div className="mt-1 text-xs text-slate-600">{r.objective}</div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </QstLayout>
  );
}

function LessonBlock({ title, body, icon: Icon, accent }) {
  if (!body) return null;
  return (
    <section>
      <h2 className="text-lg font-bold text-[#0B2545] inline-flex items-center gap-2">
        <Icon className="w-5 h-5" style={{ color: accent }} /> {title}
      </h2>
      <p className="mt-3 text-slate-800 leading-relaxed">{body}</p>
    </section>
  );
}

function LessonList({ title, items, icon: Icon, accent, numbered }) {
  if (!items || items.length === 0) return null;
  return (
    <section>
      <h2 className="text-lg font-bold text-[#0B2545] inline-flex items-center gap-2">
        <Icon className="w-5 h-5" style={{ color: accent }} /> {title}
      </h2>
      <ul className={`mt-3 space-y-2 ${numbered ? "" : ""}`}>
        {items.map((it, i) => (
          <li key={i} className="flex items-start gap-3">
            {numbered ? (
              <span className="w-6 h-6 rounded-full text-white text-xs font-bold grid place-items-center flex-shrink-0" style={{ background: accent }}>
                {i + 1}
              </span>
            ) : (
              <span className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0" style={{ background: accent }} />
            )}
            <span className="text-slate-800 leading-relaxed">{it}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/* Fullscreen field-friendly presentation mode. */
function PresentationMode({ lesson, onExit }) {
  const [page, setPage] = useState(0);
  const pages = [
    { kind: "title" },
    { kind: "problem" },
    ...(lesson.correct_procedure || []).map((_, i) => ({ kind: "step", i })),
    ...(lesson.discussion_questions || []).map((_, i) => ({ kind: "q", i })),
    { kind: "attendance" },
  ];
  const total = pages.length;
  const p = pages[page];

  return (
    <div className="fixed inset-0 bg-[#0B2545] text-white z-50 flex flex-col" data-testid="qst-lesson-presenting">
      <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
        <div className="text-xs font-bold uppercase tracking-widest text-[#F97316]">Present · {lesson.title}</div>
        <div className="text-xs">{page + 1} / {total} · ~{lesson.estimated_minutes} min</div>
        <button onClick={onExit} className="text-sm text-slate-300 hover:text-white font-bold">Exit ✕</button>
      </div>
      <div className="flex-1 flex flex-col items-center justify-center px-8 text-center">
        {p.kind === "title" && (
          <>
            <div className="text-xs uppercase tracking-widest text-[#F97316] font-bold">Today's Talk</div>
            <h1 className="mt-4 text-5xl sm:text-6xl font-black leading-tight max-w-4xl">{lesson.title}</h1>
            <p className="mt-6 text-xl text-slate-300 max-w-3xl">{lesson.objective}</p>
          </>
        )}
        {p.kind === "problem" && (
          <>
            <div className="text-xs uppercase tracking-widest text-[#F97316] font-bold">The Problem</div>
            <div className="mt-4 text-3xl sm:text-4xl font-bold leading-tight max-w-4xl">{lesson.problem}</div>
          </>
        )}
        {p.kind === "step" && (
          <>
            <div className="text-xs uppercase tracking-widest text-[#F97316] font-bold">Step {p.i + 1} of {lesson.correct_procedure.length}</div>
            <div className="mt-6 text-3xl sm:text-5xl font-bold leading-tight max-w-4xl">{lesson.correct_procedure[p.i]}</div>
          </>
        )}
        {p.kind === "q" && (
          <>
            <div className="text-xs uppercase tracking-widest text-[#F97316] font-bold">Discussion — Question {p.i + 1}</div>
            <div className="mt-6 text-3xl sm:text-5xl font-bold leading-tight max-w-4xl">{lesson.discussion_questions[p.i]}</div>
          </>
        )}
        {p.kind === "attendance" && (
          <>
            <div className="text-xs uppercase tracking-widest text-[#F97316] font-bold">Close-Out</div>
            <div className="mt-6 text-4xl sm:text-5xl font-bold">Record attendance & any action items.</div>
            <button className="mt-8 rounded-md bg-[#F97316] hover:bg-[#EA580C] text-white px-8 py-3.5 text-lg font-black">
              Save Meeting Record
            </button>
          </>
        )}
      </div>
      <div className="flex items-center justify-between px-6 py-5 border-t border-white/10">
        <button disabled={page === 0} onClick={() => setPage(page - 1)}
          className="rounded-md border border-white/20 hover:bg-white/10 disabled:opacity-30 px-5 py-2.5 font-bold">
          ← Previous
        </button>
        <div className="text-sm text-slate-400">Reviewed {lesson.last_reviewed}</div>
        <button disabled={page >= total - 1} onClick={() => setPage(page + 1)}
          className="rounded-md bg-[#F97316] hover:bg-[#EA580C] disabled:opacity-30 px-5 py-2.5 font-bold">
          Next →
        </button>
      </div>
    </div>
  );
}
